import * as THREE from 'three';
import { ToolBase, ToolPointer } from './tool';
import { Vec3, vec, add, sub, dot, cross, normalize, mul, projectPointOnPlane, dist } from '../core/vec';
import { cloneEntities, eraseEdges } from '../core/ops';
import { faceBasis } from '../core/face';
import { formatAngle, parseAngle } from '../core/units';

/** Raster beim Ziehen: Winkel rasten nahe 15°-Schritten ein (wie SketchUps Winkelmesser). */
const ANGLE_SNAP_STEP = Math.PI / 12;
const ANGLE_SNAP_TOLERANCE = (3 * Math.PI) / 180;

const COLOR_CIRCLE = 0x8a939c;
const COLOR_REF = 0xe0552c;
const COLOR_CURSOR = 0x2b2f33;

/** Drehen (Q): Mittelpunkt, Referenzstrahl, Winkel — wie SketchUps Winkelmesser. */
export class RotateTool extends ToolBase {
  readonly id = 'rotate';
  readonly label = 'Drehen';
  readonly shortcut = 'Q';
  readonly cursor = 'crosshair';
  readonly hint = 'Mittelpunkt klicken → Winkel ziehen (rastet bei 15°) oder Zahl + Enter · Strg = Kopie';

  private stage: 'center' | 'ref' | 'rotate' = 'center';
  private center: Vec3 = vec();
  private ref: Vec3 = vec();
  private normal: Vec3 = vec(0, 0, 1);
  private originals: { id: number; p: Vec3 }[] = [];
  private previewAngle = 0;
  /** Undo-Schritt erst bei der ersten echten Drehung sichern (Klick ohne Zug = kein leerer Schritt). */
  private editBegun = false;
  /** Cursorprojektion auf die Drehebene im „ref“-Stadium — die noch nicht bestätigte Referenzrichtung. */
  private refHover: Vec3 | null = null;
  private copyMode = false;
  private copy: { edges: Set<number>; faces: Set<number>; prior: { edges: Set<number>; faces: Set<number> } } | null = null;

  enter(): void {
    this.stage = 'center';
    this.originals = [];
    this.refHover = null;
    this.editBegun = false;
    if (this.ctx.selection.isEmpty) {
      this.ctx.ui.setStatus('Erst etwas auswählen (Leertaste), dann drehen');
    }
  }

  exit(): void {
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  private captureSelection(): boolean {
    const ids = this.ctx.selection.vertexIds(this.ctx.model);
    if (ids.size === 0) return false;
    this.originals = [...ids].map((id) => ({ id, p: this.ctx.model.vertexPosition(id) }));
    return true;
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    if (this.stage === 'ref') {
      // Winkelmesser folgen lassen: der Cursor zeigt live, wo die 0°-Achse liegen würde
      this.refHover = this.projectOnPlane(e);
      this.drawProtractor(this.refHover, null, null, false);
    } else if (this.stage === 'rotate') {
      const cur = this.projectOnPlane(e);
      const { angle, snapped } = this.angleFrom(cur);
      if (Math.abs(angle) > 1e-12) this.ensureRotationEdit();
      this.previewAngle = angle;
      this.applyRotation(angle);
      this.drawProtractor(cur, this.ref, angle, snapped);
      this.ctx.ui.setStatus(
        `Winkel: ${formatAngle((angle * 180) / Math.PI)}${snapped ? ' · eingerastet' : ''} · Klicken = übernehmen · Zahl tippen + Enter = exakt`
      );
    }
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    if (this.ctx.selection.isEmpty) return;
    if (this.stage === 'center') {
      if (this.originals.length === 0 && !this.captureSelection()) return;
      this.copyMode = e.ctrl;
      const snap = this.ctx.inference.snap(e.x, e.y, e.ray, { fallbackPlane: null });
      this.center = snap.point;
      // Winkelmesser-Ebene: Fläche unter dem Cursor, sonst Boden
      const faceId = this.ctx.renderer.pickFace(e.ndcX, e.ndcY, this.ctx.camera.camera);
      if (faceId !== null) {
        const face = this.ctx.model.faces.get(faceId);
        if (face) this.normal = faceBasis(face.outer.map((id) => this.ctx.model.vertexPosition(id))).n;
      } else {
        this.normal = vec(0, 0, 1);
      }
      this.refHover = null;
      this.stage = 'ref';
      this.ctx.ui.setStatus('Referenz: Maus richtet die 0°-Achse · Klicken setzt sie · Zahl + Enter dreht sofort');
    } else if (this.stage === 'ref') {
      this.refHover = this.projectOnPlane(e);
      this.beginRotation(this.refHover, e.ctrl);
    } else {
      this.rotateGroupAxes(this.previewAngle);
      this.copy = null;
      this.copyMode = false;
      this.editBegun = false;
      this.stage = 'center';
      this.originals = [];
      this.refHover = null;
      this.ctx.preview.clear();
      this.ctx.ui.setStatus(null);
    }
    this.ctx.requestUpdate();
  }

  /**
   * Referenzrichtung übernehmen und in den Winkel-Zug übergehen (Klick auf die Referenz
   * oder getippter Winkel direkt nach dem Mittelpunkt). false, wenn die Richtung entfällt.
   */
  private beginRotation(ref: Vec3 | null, ctrlToo = false): boolean {
    if (!ref || dist(ref, this.center) < 1e-6) return false;
    this.ref = ref;
    this.stage = 'rotate';
    this.previewAngle = 0;
    this.editBegun = false;
    this.copyMode = this.copyMode || ctrlToo;
    this.ctx.ui.setStatus('Winkel ziehen · Zahl tippen + Enter = exakt · Klicken = übernehmen');
    return true;
  }

  /**
   * Undo-Schritt sichern und nötigenfalls die Kopie anlegen — genau dann, wenn jetzt
   * wirklich gedreht wird. Strg = gedrehte Kopie statt Original (wie SketchUp), und
   * Kopie + Drehung bilden gemeinsam EINEN Undo-Schritt.
   */
  private ensureRotationEdit(): void {
    if (this.editBegun) return;
    this.ctx.beginEdit();
    this.editBegun = true;
    if (this.copyMode && !this.copy) {
      const sel = this.ctx.selection;
      const prior = sel.snapshot();
      const cloned = cloneEntities(this.ctx.model, sel.edges, sel.faces);
      sel.set(cloned.edges, cloned.faces);
      this.copy = { edges: cloned.edges, faces: cloned.faces, prior };
      this.originals = [];
      this.captureSelection();
    }
  }

  /** Winkelmesser zeichnen: Kreis auf der Drehebene, 0°-Strahl (orange) und Cursorstrahl. */
  private drawProtractor(cursor: Vec3 | null, ref: Vec3 | null, _angle: number | null, snapped: boolean): void {
    const n = normalize(this.normal);
    const helper = Math.abs(n.z) < 0.9 ? vec(0, 0, 1) : vec(1, 0, 0);
    const u = normalize(cross(n, helper));
    const v = cross(n, u);
    const radius = cursor ? dist(cursor, this.center) : ref ? dist(ref, this.center) : 0;
    if (radius > 1e-6) {
      const circle: Vec3[] = [];
      for (let i = 0; i <= 48; i++) {
        const t = (i / 48) * Math.PI * 2;
        circle.push(add(this.center, add(mul(u, Math.cos(t) * radius), mul(v, Math.sin(t) * radius))));
      }
      this.ctx.preview.polyline(circle, COLOR_CIRCLE, true);
    }
    if (ref) this.ctx.preview.polyline([this.center, ref], COLOR_REF);
    if (cursor) this.ctx.preview.polyline([this.center, cursor], COLOR_CURSOR);
    this.ctx.preview.dot(this.center, snapped ? 0x2e8b57 : COLOR_REF, 6);
    if (cursor) this.ctx.preview.dot(cursor, COLOR_CURSOR, 5);
  }

  private projectOnPlane(e: ToolPointer): Vec3 | null {
    return projectPointOnPlane(e.ray.origin, e.ray.dir, this.center, this.normal);
  }

  private angleFrom(cur: Vec3 | null): { angle: number; snapped: boolean } {
    if (!cur) return { angle: this.previewAngle, snapped: false };
    const v1 = normalize(sub(this.ref, this.center));
    const v2 = normalize(sub(cur, this.center));
    const raw = Math.atan2(dot(cross(v1, v2), normalize(this.normal)), dot(v1, v2));
    const snappedAngle = Math.round(raw / ANGLE_SNAP_STEP) * ANGLE_SNAP_STEP;
    const snapped = Math.abs(raw - snappedAngle) < ANGLE_SNAP_TOLERANCE;
    return { angle: snapped ? snappedAngle : raw, snapped };
  }

  private applyRotation(angle: number): void {
    const axis = new THREE.Vector3(this.normal.x, this.normal.y, this.normal.z).normalize();
    const q = new THREE.Quaternion().setFromAxisAngle(axis, angle);
    const c = new THREE.Vector3(this.center.x, this.center.y, this.center.z);
    for (const o of this.originals) {
      const p = new THREE.Vector3(o.p.x, o.p.y, o.p.z).sub(c).applyQuaternion(q).add(c);
      const v = this.ctx.model.vertices.get(o.id);
      if (v) v.p = vec(p.x, p.y, p.z);
    }
  }

  /** Gewählte Gruppen drehen ihre Achsen mit — ihr Umriss bleibt am Objekt ausgerichtet (wie SketchUp). */
  private rotateGroupAxes(angle: number): void {
    const model = this.ctx.model;
    const groups = model.groupsAt(this.ctx.selection.edges, this.ctx.selection.faces);
    if (groups.size === 0 || Math.abs(angle) < 1e-9) return;
    const axis = new THREE.Vector3(this.normal.x, this.normal.y, this.normal.z).normalize();
    const q = new THREE.Quaternion().setFromAxisAngle(axis, angle);
    model.rotateGroupAxes(groups, (v) => {
      const r = new THREE.Vector3(v.x, v.y, v.z).applyQuaternion(q);
      return vec(r.x, r.y, r.z);
    });
  }

  pointerUp(_e: ToolPointer): void {}

  wantsVcb(): boolean {
    // Schon nach dem Mittelpunkt-Klick tippen: der Cursor legt dann die 0°-Achse fest
    return this.stage === 'ref' || this.stage === 'rotate';
  }

  vcbDisplay(): string | null {
    if (this.stage !== 'rotate') return null;
    return formatAngle((this.previewAngle * 180) / Math.PI);
  }

  applyVcb(raw: string): void {
    const deg = parseAngle(raw);
    if (deg === null) return;
    if (this.stage === 'ref') {
      if (!this.beginRotation(this.refHover)) {
        this.ctx.ui.setStatus('Erst die Maus bewegen — die Cursorrichtung legt die 0°-Achse fest');
        return;
      }
    }
    if (this.stage !== 'rotate') return;
    const rad = (deg * Math.PI) / 180;
    if (Math.abs(rad) > 1e-12) this.ensureRotationEdit();
    this.applyRotation(rad);
    this.rotateGroupAxes(rad);
    this.copy = null;
    this.copyMode = false;
    this.editBegun = false;
    this.stage = 'center';
    this.originals = [];
    this.refHover = null;
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  cancel(): void {
    if (this.stage === 'rotate') this.applyRotation(0);
    // Abgebrochene Kopie wieder entfernen
    if (this.copy) {
      for (const id of this.copy.faces) this.ctx.model.deleteFace(id);
      eraseEdges(this.ctx.model, this.copy.edges);
      this.ctx.model.pruneOrphanVertices();
      this.ctx.selection.restore(this.copy.prior);
      this.copy = null;
    }
    this.copyMode = false;
    this.stage = 'center';
    this.originals = [];
    this.refHover = null;
    this.editBegun = false;
    this.previewAngle = 0;
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}
